import React, { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import {
  ActiveSuggestedAction,
  SUGGESTED_ACTION_TYPE,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { useTranslation } from 'react-i18next';
import useGetThreeDotsOptions from '../../hooks/useGetThreeDotsOptions';
import useGetSuggestedAction from '../../hooks/useGetSuggestedAction';
import { generateDaysLeftString } from 'dateUtils';
import {
  ACTIVE_SUGGESTION_ACTION,
  ACTIVE_SUGGESTION_DESCRIPTION,
  ACTIVE_SUGGESTION_TITLE,
} from '../../data';
import { SuggestedActionUndoHandlers } from '../../hooks/useGetUndoActions';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { CreateFaqRef } from 'router/subrouters/DataRoom/pages/Faq/components/CreateFaqDrawer/CreateFaqDrawer.component';
import CreateFaqDrawer from 'router/subrouters/DataRoom/pages/Faq/components/CreateFaqDrawer';
import {
  isNoFaqsSuggestion,
  isSecureLinkSuggestion,
  isUnrepliedQuestionsSuggestion,
  removeActiveSuggestedAction,
} from '../../utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { WriteAnswerDrawer } from 'router/subrouters/DataRoom/components/QAndASellerContainer/components/WriteAnswerDrawer';
import useProlongSecureLink from '../../hooks/useProlongSecureLink';
import useSuggestedActionQuestion from '../../hooks/useSuggestedActionQuestion';
import useActionedSuggestedAction from '../../hooks/useActionedSuggestedAction';
import SuggestedActionItemWrapper from 'components/SuggestedActionItemWrapper';

import './SuggestedActionItem.styles.scss';

type SuggestedActionItemProps = {
  className?: string;
  suggestion: ActiveSuggestedAction;
} & SuggestedActionUndoHandlers;

const SuggestedActionItem: React.FC<SuggestedActionItemProps> = (props) => {
  const { className, suggestion, ...rest } = props;

  const { type, count, names, expiredAt, dataRoomQuestionId } = suggestion;

  const [faqDrawer, faqDrawerRef] = useCallbackRef<CreateFaqRef>(null);
  const [answerQuestionDrawer, answerQuestionDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);

  const queryClient = useQueryClient();

  const { canUserAddFAQ } = useContext(DataRoomContext);

  const title = ACTIVE_SUGGESTION_TITLE[type];
  const description = ACTIVE_SUGGESTION_DESCRIPTION[type];
  const action = ACTIVE_SUGGESTION_ACTION[type];

  const time = generateDaysLeftString(expiredAt);
  const secureLink = names?.[0];

  const { t } = useTranslation();

  const { isDissmising, isDontReminding, threeDotsOptions } =
    useGetThreeDotsOptions(suggestion, rest);

  const actionSuggestedAction = useActionedSuggestedAction();

  const actionCurrentSuggestion = useCallback(
    () => actionSuggestedAction(suggestion),
    [actionSuggestedAction, suggestion],
  );

  const { openProlongModal, prolongSecureLinkModal } = useProlongSecureLink(
    actionCurrentSuggestion,
  );

  const {
    question,
    isFetching: isFetchingQuestion,
    openQuestionDrawer,
  } = useSuggestedActionQuestion({
    dataRoomQuestionId,
    drawer: answerQuestionDrawer,
  });

  const openFaqDrawer = useCallback(
    () => faqDrawer?.setOpen(true),
    [faqDrawer],
  );

  const removeNoFaqsSuggestion = useCallback(() => {
    actionCurrentSuggestion();
    removeActiveSuggestedAction(queryClient, [SUGGESTED_ACTION_TYPE.NO_FAQS]);
  }, [actionCurrentSuggestion, queryClient]);

  const removeUnrepliedQuestionsSuggestion = useCallback(() => {
    actionCurrentSuggestion();
    removeActiveSuggestedAction(queryClient, [
      SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS,
    ]);
  }, [queryClient, actionCurrentSuggestion]);

  const classes = classNames(
    'suggested-action-item',
    { loading: isDissmising || isDontReminding || isFetchingQuestion },
    className,
  );

  const onAction = useGetSuggestedAction(
    suggestion,
    openProlongModal,
    openFaqDrawer,
    openQuestionDrawer,
    actionCurrentSuggestion,
  );

  return (
    <SuggestedActionItemWrapper
      className={classes}
      type={type}
      title={t(title, { count, secureLink, time })}
      description={t(description, { count, secureLink })}
      threeDotsOptions={threeDotsOptions}
      buttonDisabled={isFetchingQuestion}
      buttonContent={t(action, { count })}
      onButtonClick={onAction}
    >
      {isSecureLinkSuggestion(type) && prolongSecureLinkModal}
      {isNoFaqsSuggestion(type) && canUserAddFAQ && (
        <CreateFaqDrawer
          ref={faqDrawerRef}
          onSuccess={removeNoFaqsSuggestion}
        />
      )}
      {isUnrepliedQuestionsSuggestion(type) && (
        <WriteAnswerDrawer
          ref={answerQuestionDrawerRef}
          question={question}
          onQuestionsUpdate={removeUnrepliedQuestionsSuggestion}
          updateQuestion={removeUnrepliedQuestionsSuggestion}
        />
      )}
    </SuggestedActionItemWrapper>
  );
};

export default SuggestedActionItem;
